import { buildChildrensBookChapterSceneNavigationV1 } from "./childrensBookChapterSceneNavigationV1";

// All operations are local, explicit and reversible. This is NOT persistence.
// Only structural labels and stable existing unit IDs enter the outline.
const clean = (x) => String(x ?? "").trim();
const fail = (code, chapters) => ({ok:false, code, chapters});
const copyOutline = (chapters) => chapters.map(chapter => ({
  id:clean(chapter.id), title:clean(chapter.title),
  scenes:chapter.scenes.map(scene=>({
    id:clean(scene.id), title:clean(scene.title), unitIds:[...scene.unitIds],
  })),
}));
function context(units, chapters) {
  const base=buildChildrensBookChapterSceneNavigationV1({units});
  if(!base.ok) return {ok:false,code:base.code};
  if(!Array.isArray(chapters)) return {ok:false,code:"invalid_outline"};
  const eligible=base.sections.find(s=>s.kind==="unassigned")?.entries||[];
  const positions=new Map(eligible.map(e=>[e.unitId,e.sourceIndex]));
  const chapterIds=new Set(),sceneIds=new Set(),used=new Set();
  let last=-1;
  for(const chapter of chapters) {
    if(!chapter || !clean(chapter.id) || !clean(chapter.title) ||
      !Array.isArray(chapter.scenes) || chapterIds.has(clean(chapter.id)))
      return {ok:false,code:"invalid_outline"};
    chapterIds.add(clean(chapter.id));
    for(const scene of chapter.scenes) {
      if(!scene || !clean(scene.id) || !clean(scene.title) ||
        !Array.isArray(scene.unitIds) || sceneIds.has(clean(scene.id)))
        return {ok:false,code:"invalid_outline"};
      sceneIds.add(clean(scene.id));
      for(const raw of scene.unitIds) {
        const id=clean(raw), pos=positions.get(id);
        if(pos===undefined || used.has(id))return {ok:false,code:"invalid_assignment"};
        if(pos<=last)return {ok:false,code:"physical_order_protected"};
        last=pos; used.add(id);
      }
    }
  }
  return {ok:true,positions,assigned:used,chapterIds,sceneIds,eligible};
}
export function previewChildrensBookChapterSceneManagementV1B({units=[],chapters=[]}={}) {
  const valid=context(units,chapters);
  if(!valid.ok) return {...valid,navigation:buildChildrensBookChapterSceneNavigationV1({units})};
  // Empty drafts have no page ownership and must not hide unassigned pages.
  const completed=chapters.map(c=>({
    id:c.id,title:c.title,
    scenes:c.scenes.filter(s=>s.unitIds.length>0)
      .map(s=>({id:s.id,title:s.title,unitIds:[...s.unitIds]})),
  })).filter(c=>c.scenes.length>0);
  return {ok:true, navigation:buildChildrensBookChapterSceneNavigationV1({units,chapters:completed})};
}
export function transitionChildrensBookChapterSceneManagementV1B({
  units=[],chapters=[],action={},
}={}) {
  const valid=context(units,chapters);
  if(!valid.ok)return fail(valid.code,chapters);
  const next=copyOutline(chapters);
  const kind=clean(action.type);
  const chapterId=clean(action.chapterId),sceneId=clean(action.sceneId);
  const chapter=next.find(c=>c.id===chapterId);
  const scene=chapter?.scenes.find(s=>s.id===sceneId);
  const title=clean(action.title), id=clean(action.id);
  const direction=Number(action.direction);
  if(kind==="add_chapter") {
    if(!id || !title || valid.chapterIds.has(id) || valid.sceneIds.has(id))
      return fail("invalid_chapter",chapters);
    next.push({id,title,scenes:[]});
  } else if(kind==="rename_chapter") {
    if(!chapter||!title)return fail("invalid_chapter",chapters);
    chapter.title=title;
  } else if(kind==="add_scene") {
    if(!chapter||!id||!title||valid.sceneIds.has(id)||valid.chapterIds.has(id))
      return fail("invalid_scene",chapters);
    chapter.scenes.push({id,title,unitIds:[]});
  } else if(kind==="rename_scene") {
    if(!scene||!title)return fail("invalid_scene",chapters);
    scene.title=title;
  } else if(kind==="assign_unit") {
    const unitId=clean(action.unitId);
    if(!scene || !valid.positions.has(unitId) || valid.assigned.has(unitId))
      return fail("invalid_assignment",chapters);
    // Preserve requested page order; existing final validation must reject
    // an earlier physical page rather than silently sorting unit identities.
    scene.unitIds.push(unitId);
  } else if(kind==="unassign_unit") {
    const unitId=clean(action.unitId);
    if(!scene||!scene.unitIds.includes(unitId))return fail("invalid_assignment",chapters);
    scene.unitIds=scene.unitIds.filter(unit=>unit!==unitId);
  } else if(kind==="move_chapter" || kind==="move_scene") {
    if(![-1,1].includes(direction))return fail("invalid_move",chapters);
    const siblings=kind==="move_chapter" ? next : chapter?.scenes;
    if(!Array.isArray(siblings))return fail("invalid_move",chapters);
    const current=siblings.findIndex(x=>x.id===(kind==="move_chapter"?chapterId:sceneId));
    const target=current+direction;
    if(current<0||target<0||target>=siblings.length)return fail("invalid_move",chapters);
    [siblings[current],siblings[target]]=[siblings[target],siblings[current]];
  } else if(kind==="remove_scene") {
    if(!scene||scene.unitIds.length!==0)return fail("scene_has_pages",chapters);
    chapter.scenes=chapter.scenes.filter(s=>s.id!==sceneId);
  } else if(kind==="remove_chapter") {
    if(!chapter||chapter.scenes.length!==0)return fail("chapter_has_scenes",chapters);
    next.splice(next.findIndex(c=>c.id===chapterId),1);
  } else {
    return fail("unknown_action",chapters);
  }
  const after=context(units,next);
  return after.ok ? {ok:true,chapters:next} : fail(after.code,chapters);
}
